import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DialogContent } from '@/components/atoms/Dialog/Dialog';
import { IconName } from '@/components/atoms/Icon/types';
import { TranslationCategory } from '@/types/management/managementCategories';

import { IconPickerCategoryPopup } from './components/IconPickerCategoryPopup';
import { ManageCategoryPopup } from './components/ManageCategoryPopup';
import { TranslationEditItem } from './components/TranslationEditItem';
import { TranslationOverviewCategoryPopup } from './components/TranslationOverviewCategory';

type CategoryStep = 'step1' | 'step1-icon' | 'step2' | 'step2-edit';

export type CategoryPopupData = {
  icon?: IconName;
  name: string;
  description: string;
  data: TranslationCategory[];
};

export type SetCategoryPopupData = Dispatch<SetStateAction<CategoryPopupData>>;

type CategoryPopupProps = {
  type: 'create' | 'edit';
  categoryData: CategoryPopupData;
  isLoadingTranslateCategory?: boolean;
  isLoadingSubmitCategory?: boolean;
  setCategoryData: SetCategoryPopupData;
  onSubmitCategory: () => Promise<void>;
  onTranslateCategory: () => Promise<void>;
};

export type SetCategoryPopupStep = Dispatch<SetStateAction<CategoryStep>>;

export const CategoryPopup: FC<CategoryPopupProps> = ({
  type,
  categoryData,
  isLoadingSubmitCategory,
  isLoadingTranslateCategory,
  setCategoryData,
  onSubmitCategory,
  onTranslateCategory,
}) => {
  const [step, setStep] = useState<CategoryStep>('step1');
  const [editingOptionValue, setEditingOptionValue] = useState<TranslationCategory | null>(null);
  const [editingData, setEditingData] = useState({
    name: '',
    description: '',
  });
  const { t } = useTranslation('components');

  const handleEditTranslation = (newValue: string, field: 'name' | 'description') => {
    setEditingData(prev => ({ ...prev, [field]: newValue }));
  };

  const handleSave = () => {
    if (!editingOptionValue) return;

    const updatedOptions = categoryData.data.map(option => {
      const isEditingOption = option.languageCode === editingOptionValue.languageCode;

      if (!isEditingOption) return option;

      return {
        ...option,
        name: editingData.name,
        description: editingData.description,
      };
    });

    setCategoryData(prev => ({ ...prev, data: updatedOptions }));
    setEditingOptionValue(null);
    setStep('step2');
  };

  const onEditClick = (option: TranslationCategory) => {
    setEditingOptionValue(option);
    setEditingData({
      name: option.name,
      description: option.description,
    });
    setStep('step2-edit');
  };

  const handleCancel = () => {
    setEditingOptionValue(null);
    setStep('step2');
  };

  const handleAddCategory = async () => {
    await onSubmitCategory();
    setStep('step1');
  };

  const handleManagePopupNextStepClick = async () => {
    await onTranslateCategory();
    setStep('step2');
  };

  const showCloseButton = step !== 'step1-icon';

  const renderCategoryState = () => {
    const getStateByStep = () => {
      switch (step) {
        case 'step1': {
          return (
            <ManageCategoryPopup
              type={type}
              categoryData={categoryData}
              isLoadingTranslateCategory={isLoadingTranslateCategory}
              setCategoryData={setCategoryData}
              setStep={setStep}
              onNextStepClick={handleManagePopupNextStepClick}
            />
          );
        }
        case 'step1-icon': {
          return <IconPickerCategoryPopup setStep={setStep} setCategoryData={setCategoryData} />;
        }
        case 'step2': {
          return (
            <TranslationOverviewCategoryPopup
              setStep={setStep}
              translationOptions={categoryData.data}
              onEditClick={onEditClick}
              onAddCategory={handleAddCategory}
              submitButtonLabel={
                type === 'create'
                  ? t('translationOverviewCategory.buttons.buttonNewCategory')
                  : t('translationOverviewCategory.buttons.buttonEditCategory')
              }
              isLoadingSubmitCategory={isLoadingSubmitCategory}
            />
          );
        }
        case 'step2-edit': {
          return (
            <TranslationEditItem
              editingOptionValue={editingOptionValue}
              editingData={editingData}
              onEditChange={handleEditTranslation}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          );
        }
      }
    };

    return getStateByStep();
  };

  return (
    <DialogContent showCloseButton={showCloseButton} className="w-[460px]">
      {renderCategoryState()}
    </DialogContent>
  );
};
